import { useId, useState } from "react";
import { refusalOf } from "./answer";
import { useSetSmsReception, useSmsInbox, useSmsPage } from "./control";
import {
  pageCount,
  receivedTime,
  SMS_PAGE_SIZE,
  turnPage,
  type SmsPaging,
} from "./sms";
import { SmsReplay } from "./SmsReplay";
import { smsVoteStatus } from "./smsVote";
import type { LivePayload } from "./types";
import { Button } from "../ui/Button";

/**
 * ═══ LE MODE SMS DE LA RÉGIE ═══
 *
 * La réception, qu'on ouvre et qu'on ferme, et les SMS qui arrivent, les plus récents
 * en haut, par pages (voir `turnPage`). Rien de ce qui est ici n'atteint la salle :
 * c'est une liste de travail pour l'organisation.
 *
 * ⚠️ LA RÉCEPTION EST UN GESTE, PAS UN ÉTAT PAR DÉFAUT. Le numéro est partagé entre
 * tous les événements : tant que personne ne l'ouvre ici, les SMS vont ailleurs (ou
 * nulle part, gardés sans événement). L'écran le dit en toutes lettres, sinon une
 * liste vide se lit « personne n'a écrit » alors qu'elle veut dire « on n'écoute pas ».
 *
 * Displayed strings are French: this is a French product.
 */
export function SmsInbox({
  live,
}: {
  /**
   * Le direct : son activité est celle sur laquelle un SMS compte (voir
   * `smsVoteStatus`), ses résultats les plus frais pour le rejeu (voir `rowResults`).
   */
  live: Pick<LivePayload, "activity" | "results">;
}) {
  const { data: inbox, isPending, isError } = useSmsInbox();
  const reception = useSetSmsReception();
  /* Replié d'office : rejouer répare après coup, ce n'est pas le geste du direct. */
  const [replaying, setReplaying] = useState(false);
  const replayId = useId();
  /* `null` sur la première page, la liste en direct. */
  const [paging, setPaging] = useState<SmsPaging | null>(null);
  const pageQuery = useSmsPage(paging);

  if (isPending) {
    return (
      <section className="rg__b">
        <p className="empty">Chargement des SMS.</p>
      </section>
    );
  }

  /* ⚠️ « AUCUN SMS » EST UNE AFFIRMATION : une lecture ratée ne la fait pas. */
  if (!inbox) {
    return (
      <section className="rg__b">
        <p className="empty">
          {isError
            ? "Les SMS n'ont pas pu être chargés. La page réessaie toute seule."
            : "Chargement des SMS."}
        </p>
      </section>
    );
  }

  const { open, openedAt, total, messages } = inbox;
  const vote = smsVoteStatus(live.activity);

  /* Au-delà de la première page, le compte sous la borne : celui du serveur dès qu'il
     l'a rendu. Ce qui est arrivé depuis est en première page, et l'écran le dit. */
  const known = paging ? pageQuery.data?.total : undefined;
  const page = paging?.page ?? 1;
  const pages = pageCount(paging ? (known ?? paging.total) : total);
  const arrivedSince = paging ? total - (known ?? paging.total) : 0;
  const shown = paging ? pageQuery.data?.messages : messages.slice(0, SMS_PAGE_SIZE);
  const go = (next: number) => setPaging(turnPage(paging, next, inbox, known));

  return (
    <section className="rg__b">
      <div className="rg__t">
        <h3>Les SMS reçus</h3>
        <span className="rg__acts">
          <Button
            variant="ghost"
            aria-expanded={replaying}
            aria-controls={replayId}
            onClick={() => setReplaying((shown) => !shown)}
          >
            Rejouer des votes
          </Button>
          <Button
            variant={open ? "ghost" : undefined}
            loading={reception.isPending}
            onClick={() => reception.mutate(!open)}
          >
            {open ? "Fermer la réception" : "Ouvrir la réception"}
          </Button>
        </span>
      </div>

      <span className="rga__meta">
        <span className="rga__st" data-etat={open ? "open" : undefined}>
          {open ? `Réception ouverte depuis ${receivedTime(openedAt)}` : "Réception fermée"}
        </span>
        <span className="rga__st">
          {total} SMS
        </span>
      </span>

      <p className="hint">
        {open
          ? "Les SMS envoyés au numéro arrivent ici en direct."
          : "Les SMS envoyés au numéro n'arrivent pas sur cet événement. Un seul événement les reçoit à la fois : ouvrir la réception ici la ferme ailleurs."}
      </p>

      {/* ⚠️ AU-DESSUS DE LA LISTE, parce que c'est là qu'on regarde quand un SMS n'a pas
          compté : sur un vote, ou sans activité ouverte, aucun ne compte, et la liste
          seule ne le montre pas (voir `smsVoteStatus`). */}
      {open ? <p className={vote.counts ? "hint" : "live__err"}>{vote.message}</p> : null}

      {/* ⚠️ DIT ICI, parce que c'est ici qu'on ouvre : ouvrir la réception change aussi
          ce qu'affiche l'écran de salle (voir `smsVote.ts`). */}
      <p className="hint">
        Réception ouverte, l'écran de salle donne un numéro à chaque réponse du sondage
        ou du quiz ouvert, avec la consigne de vote : un SMS qui porte ce numéro compte
        comme un vote, un seul par numéro. Un SMS qui ne porte pas un numéro de réponse ne
        compte pas, et n'empêche pas d'en envoyer un autre. Le numéro affiché se règle sur
        l'écran de salle.
      </p>

      {reception.data && reception.data.alsoClosed > 0 ? (
        <p className="hint">
          La réception était ouverte sur un autre événement : elle y a été fermée.
        </p>
      ) : null}

      {reception.isError ? (
        <p className="live__err" role="alert">
          {refusalOf(reception.error)}
        </p>
      ) : null}

      {/* ⚠️ AU-DESSUS DE LA LISTE : des centaines de SMS la rendent longue, et une
          commande posée dessous sortirait de l'écran. */}
      <SmsReplay
        id={replayId}
        hidden={!replaying}
        live={live}
        messages={messages}
        total={total}
      />

      {messages.length === 0 ? (
        <p className="empty">
          {open
            ? "Aucun SMS reçu pour l'instant."
            : "Aucun SMS reçu sur cet événement. Ouvrez la réception pour commencer."}
        </p>
      ) : (
        <>
          {/* ⚠️ AU-DESSUS DE LA LISTE, pour la même raison que le rejeu : cinquante SMS
              la rendent encore longue. */}
          {pages > 1 ? (
            <nav className="smsp" aria-label="Pages des SMS">
              <Button variant="ghost" disabled={page === 1} onClick={() => go(1)}>
                Première page
              </Button>
              <Button variant="ghost" disabled={page === 1} onClick={() => go(page - 1)}>
                Page précédente
              </Button>
              <span className="smsp__n">
                Page {page} sur {pages}
              </span>
              <Button
                variant="ghost"
                disabled={page >= pages}
                onClick={() => go(page + 1)}
              >
                Page suivante
              </Button>
              <Button variant="ghost" disabled={page >= pages} onClick={() => go(pages)}>
                Dernière page
              </Button>
            </nav>
          ) : null}

          {arrivedSince > 0 ? (
            <p className="hint">
              {arrivedSince > 1
                ? `${arrivedSince} SMS arrivés depuis, en première page.`
                : "1 SMS arrivé depuis, en première page."}
            </p>
          ) : null}

          {shown ? (
            /* `role="log"` sur la première page : une liste qui grandit par le haut,
               annoncée poliment, sans relire tout ce qui était déjà là. Les suivantes
               ne bougent pas. */
            <ol
              className="sms"
              role={paging ? undefined : "log"}
              aria-label="SMS reçus"
              aria-busy={(paging && pageQuery.isPlaceholderData) || undefined}
            >
              {shown.map((message) => (
                <li key={message.id} className="sms__i">
                  <span className="sms__m">
                    <time dateTime={message.receivedAt}>
                      {receivedTime(message.receivedAt)}
                    </time>
                    <span>{message.phone || "Numéro inconnu"}</span>
                  </span>
                  <p className="sms__t">{message.text || "(SMS vide)"}</p>
                </li>
              ))}
            </ol>
          ) : pageQuery.isError ? (
            <p className="live__err" role="alert">
              Cette page de SMS n'a pas pu être chargée.{" "}
              <Button variant="ghost" onClick={() => void pageQuery.refetch()}>
                Réessayer
              </Button>
            </p>
          ) : (
            <p className="empty">Chargement des SMS.</p>
          )}
        </>
      )}
    </section>
  );
}
